import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from '../firebase';

export default function Dashboard({ user }) {
  const [profile, setProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({total:0,pending:0,completed:0,volunteers:0});
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      const snap = await getDoc(doc(db,'users',user.uid));
      if(snap.exists()) setProfile(snap.data());
      const q = query(collection(db,'requests'),orderBy('created_at','desc'),limit(5));
      const qs = await getDocs(q);
      setRequests(qs.docs.map(d=>({id:d.id,...d.data()})));
    };
    load();
  }, [user]);

  const handleLogout = async () => { await signOut(auth); navigate('/'); };

  const urgencyColor = {urgent:'badge-red',normal:'badge-orange',low:'badge-green'};

  return (
    <div style={{minHeight:'100vh',background:'#F0FDF4'}}>
      <nav className="nav">
        <Link className="nav-brand" to="/"><div className="nav-logo">🏘</div><div className="nav-name">Community<span>Bridge</span></div></Link>
        <div className="nav-links">
          <Link to="/dashboard" className="nav-link active">Home</Link>
          <Link to="/requests" className="nav-link">Requests</Link>
          <Link to="/events" className="nav-link">Events</Link>
          <Link to="/volunteers" className="nav-link">Volunteers</Link>
          {profile?.role==='admin' && <Link to="/admin" className="nav-link">Admin</Link>}
          <button onClick={handleLogout} className="btn btn-gray" style={{padding:'6px 14px',fontSize:'13px'}}>Logout</button>
        </div>
      </nav>

      <div className="page">
        {profile?.status==='pending' && (
          <div className="alert alert-orange" style={{marginBottom:'1.5rem'}}>
            ⏳ <strong>Account pending approval.</strong> Our admin team is reviewing your profile. You'll be notified soon.
          </div>
        )}

        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'2rem',flexWrap:'wrap',gap:'1rem'}}>
          <div>
            <h1 className="page-title">👋 Welcome, {profile?.name || user.displayName}!</h1>
            <p className="page-sub">Here's what's happening in your community today.</p>
          </div>
          <div style={{display:'flex',gap:'8px'}}>
            <Link to="/requests" className="btn btn-primary">+ Post Request</Link>
            <button className="btn btn-danger" style={{fontSize:'13px'}} onClick={()=>alert('🆘 SOS Alert sent to nearby volunteers!')}>🆘 SOS</button>
          </div>
        </div>

        <div className="grid4" style={{marginBottom:'2rem'}}>
          {[{icon:'🆘',num:requests.filter(r=>r.status==='open').length,label:'Open Requests'},{icon:'🤝',num:requests.filter(r=>r.status==='completed').length,label:'Completed'},{icon:'👥',num:'24',label:'Nearby Volunteers'},{icon:'⭐',num:profile?.rating||'New',label:'Your Rating'}].map((s,i)=>(
            <div key={i} className="stat-card">
              <div style={{fontSize:'28px',marginBottom:'6px'}}>{s.icon}</div>
              <div className="stat-num">{s.num}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="grid2">
          <div>
            <h2 style={{fontSize:'18px',fontWeight:700,marginBottom:'1rem'}}>Recent Requests</h2>
            {requests.length===0 ? (
              <div className="card" style={{textAlign:'center',padding:'2rem',color:'#6B7280'}}>
                <div style={{fontSize:'32px',marginBottom:'8px'}}>📭</div>
                <p>No requests yet. <Link to="/requests" style={{color:'#16A34A'}}>Post the first one!</Link></p>
              </div>
            ) : requests.map(r=>(
              <div key={r.id} className="card" style={{marginBottom:'10px',padding:'1rem'}}>
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'8px'}}>
                  <div style={{flex:1}}>
                    <p style={{fontWeight:600,fontSize:'14px',marginBottom:'4px'}}>{r.title}</p>
                    <p style={{fontSize:'12px',color:'#6B7280'}}>📍 {r.city} · {r.category}</p>
                  </div>
                  <span className={`badge ${urgencyColor[r.urgency]||'badge-gray'}`}>{r.urgency}</span>
                </div>
                {r.budget_max>0 && <p style={{fontSize:'12px',color:'#16A34A',marginTop:'6px',fontWeight:600}}>💰 RM{r.budget_min}-{r.budget_max}</p>}
              </div>
            ))}
            <Link to="/requests" className="btn btn-secondary" style={{width:'100%',justifyContent:'center',marginTop:'8px'}}>View All Requests</Link>
          </div>

          <div>
            <h2 style={{fontSize:'18px',fontWeight:700,marginBottom:'1rem'}}>Your Profile</h2>
            <div className="card">
              <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'1rem'}}>
                <div style={{width:'56px',height:'56px',borderRadius:'50%',background:'linear-gradient(135deg,#16A34A,#EA580C)',display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontSize:'22px',fontWeight:800,flexShrink:0}}>
                  {(profile?.name||'U')[0].toUpperCase()}
                </div>
                <div>
                  <p style={{fontWeight:700,fontSize:'16px'}}>{profile?.name}</p>
                  <p style={{fontSize:'13px',color:'#6B7280',textTransform:'capitalize'}}>{profile?.role} · {profile?.city||'Location not set'}</p>
                </div>
              </div>
              <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginBottom:'1rem'}}>
                <span className={`badge ${profile?.status==='approved'?'badge-green':profile?.status==='pending'?'badge-orange':'badge-red'}`}>
                  {profile?.status==='approved'?'✅ Verified':profile?.status==='pending'?'⏳ Pending':'❌ Rejected'}
                </span>
                <span className="badge badge-blue">⭐ {profile?.rating||0} rating</span>
              </div>
              {(profile?.skills||[]).length>0 && (
                <div style={{display:'flex',flexWrap:'wrap',gap:'4px'}}>
                  {profile.skills.map(s=><span key={s} className="badge badge-gray">{s}</span>)}
                </div>
              )}
              {profile?.status==='pending' && (
                <div style={{marginTop:'1rem',padding:'10px',background:'#FEF3C7',borderRadius:'10px',fontSize:'13px',color:'#B45309'}}>
                  📎 Upload ID card to speed up verification
                </div>
              )}
            </div>

            <h2 style={{fontSize:'18px',fontWeight:700,margin:'1.5rem 0 1rem'}}>Quick Actions</h2>
            <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
              <Link to="/requests" className="btn btn-primary" style={{justifyContent:'center'}}>🆘 Post Help Request</Link>
              <Link to="/events" className="btn btn-orange" style={{justifyContent:'center'}}>📅 Create Event</Link>
              <Link to="/volunteers" className="btn btn-secondary" style={{justifyContent:'center'}}>👥 Browse Volunteers</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
